"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, TrendingUp, Target, PhoneCall, CircleCheck as CheckCircle2, Clock } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const resultsData = [{
  id: "tech-seo",
  badge: "Technical SEO Only",
  icon: TrendingUp,
  primaryMetric: "+128%",
  primaryLabel: "organic clicks growth",
  secondaryMetric: "+86%",
  secondaryLabel: "search impressions increase",
  timeframe: "3 months turnaround",
  clientType: "UK B2B & Service Business",
  detail: "Achieved without creating new content or building backlinks. Average search position lifted from 13.6 to 11.4 strictly through Core Web Vitals optimisation, crawl budget cleanup, indexation fixes, and mobile page speed improvements.",
  highlight: "Zero new content or backlinks required"
}, {
  id: "strategic-leadership",
  badge: "Fractional SEO Leadership",
  icon: Target,
  primaryMetric: "50+",
  primaryLabel: "page-one search positions",
  secondaryMetric: "12 mo",
  secondaryLabel: "sustained compounding ranking",
  timeframe: "12 months continuous strategy",
  clientType: "UK Growing Commercial Brand",
  detail: "Positioned small businesses to outrank established competitors for high-intent search terms through structured schema markup, precision keyword mapping, conversion tracking, and bottom-of-funnel content architecture.",
  highlight: "Direct commercial enquiries generated"
}, {
  id: "local-seo",
  badge: "Local SEO & Revenue",
  icon: PhoneCall,
  primaryMetric: "+380%",
  primaryLabel: "direct inbound phone calls",
  secondaryMetric: "+£3.8k",
  secondaryLabel: "new monthly recurring revenue",
  timeframe: "90 days implementation",
  clientType: "UK Regional Service Provider",
  detail: "Transformed local visibility by restructuring Google Business Profile categories, optimising local area landing pages, earning genuine local citations, and building localised schema for high-ticket service queries.",
  highlight: "+£3,800 added monthly revenue in 90 days"
}];
const timelinePoints = [{
  phase: "Weeks 1–6",
  title: "Technical triage & audit fixes",
  desc: "Resolving crawl blockers, indexing errors, and mobile performance bottlenecks to unstick existing keyword positions."
}, {
  phase: "Weeks 6–12",
  title: "Early momentum & rank jumps",
  desc: "Search engines recrawl cleaned pages; targeted commercial pages begin moving into initial page-one and page-two positions."
}, {
  phase: "Months 3–12",
  title: "Compounding revenue growth",
  desc: "Authority compounding and targeted content pipelines generate predictable, high-value organic enquiries month after month."
}];
export default function ProvenResults() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="provenresults" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      {/* Decorative ambient background accents */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-96 bg-secondary/30 blur-3xl -z-10 pointer-events-none rounded-full" />
      <div className="absolute bottom-10 right-0 w-96 h-96 bg-muted blur-3xl -z-10 pointer-events-none rounded-full" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16 lg:mb-20">
          <Badge variant="secondary" className="mb-4 text-xs font-semibold uppercase tracking-wider px-3 py-1">
            Measurable Commercial Impact
          </Badge>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground mb-6">
            Proven results for UK businesses
          </h2>
          <p className="font-sans text-base sm:text-lg text-muted-foreground leading-relaxed">
            Results speak louder than claims. Every metric below comes directly from verified client campaigns led by
            Yassine Essaadi, focusing strictly on bottom-line business value rather than hollow vanity traffic.
          </p>
        </div>

        {/* 3 Metric / Proof Cards */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {resultsData.map((result, i) => {
          const IconComponent = result.icon;
          return <div key={result.id} data-index={i} className="h-full">
                <motion.div variants={itemVariants} className="h-full">
                  <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300 flex flex-col justify-between relative overflow-hidden group">
                    {/* Subtle top indicator bar */}
                    <div className="absolute top-0 left-0 right-0 h-1 bg-primary group-hover:bg-accent transition-colors duration-300" />

                    <div>
                      <CardHeader className="pt-7 pb-4">
                        <div className="flex items-center justify-between gap-2 mb-3">
                          <Badge variant="outline" className="text-xs font-medium border-border">
                            {result.badge}
                          </Badge>
                          <div className="p-2 rounded-md bg-secondary text-primary">
                            <IconComponent className="size-4" />
                          </div>
                        </div>
                        <CardTitle className="font-serif text-xl font-bold text-foreground">
                          {result.clientType}
                        </CardTitle>
                        <CardDescription className="flex items-center gap-1.5 text-xs text-muted-foreground pt-1">
                          <Clock className="size-3.5 text-accent" />
                          <span>{result.timeframe}</span>
                        </CardDescription>
                      </CardHeader>

                      <CardContent className="space-y-6 pt-2">
                        {/* Primary & Secondary Stat Display */}
                        <div className="grid grid-cols-2 gap-4 p-4 rounded-lg bg-background border border-border">
                          <div>
                            <div className="font-serif text-3xl sm:text-4xl font-extrabold text-primary tracking-tight">
                              {result.primaryMetric}
                            </div>
                            <div className="text-xs font-medium text-muted-foreground mt-1 leading-tight">
                              {result.primaryLabel}
                            </div>
                          </div>
                          <div className="border-l border-border pl-4">
                            <div className="font-serif text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
                              {result.secondaryMetric}
                            </div>
                            <div className="text-xs font-medium text-muted-foreground mt-1 leading-tight">
                              {result.secondaryLabel}
                            </div>
                          </div>
                        </div>

                        {/* Narrative Detail */}
                        <p className="text-sm text-foreground/80 leading-relaxed font-sans">
                          {result.detail}
                        </p>
                      </CardContent>
                    </div>

                    <div className="px-6 pb-6 pt-2">
                      <Separator className="mb-4" />
                      <div className="flex items-center gap-2 text-xs font-medium text-primary">
                        <CheckCircle2 className="size-4 text-accent shrink-0" />
                        <span>{result.highlight}</span>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              </div>;
        })}
        </motion.div>

        {/* Narrative Context & Timeline Breakdown */}
        <div className="rounded-2xl bg-secondary/50 border border-border p-8 lg:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left Explanation Column */}
            <div className="lg:col-span-6 space-y-4">
              <Badge variant="secondary" className="bg-background text-foreground font-semibold text-xs border border-border">
                Strategy & Accountability
              </Badge>
              <h3 className="font-serif text-2xl sm:text-3xl font-bold text-foreground">
                Revenue-first SEO, never traffic for its own sake
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed font-sans">
                These outcomes illustrate what becomes possible with senior fractional SEO leadership. You secure
                director-level search strategy and execution at a fraction of the cost of a full-time hire, giving you
                flexible senior expertise without overhead or long-term commitments.
              </p>
              <p className="text-muted-foreground text-sm leading-relaxed font-sans">
                Typical engagements span 3 to 12 months. Most clients experience clear search momentum in the first 6 to
                12 weeks through high-impact technical remediation, followed by compounding revenue returns through
                content and authority building.
              </p>
              <div className="pt-2 flex flex-wrap items-center gap-4">
                <Button asChild size="lg" className="font-medium shadow-sm">
                  <Link to="/CaseStudiesPage" className="inline-flex items-center gap-2">
                    <span>Read genuine case studies</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="font-medium bg-background hover:bg-card">
                  <Link to="/ContactPage">
                    <span>Book a consultation</span>
                  </Link>
                </Button>
              </div>
            </div>

            {/* Right Engagement Progression Column */}
            <div className="lg:col-span-6 space-y-4">
              <div className="bg-card rounded-xl p-6 border border-border space-y-5 shadow-xs">
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Typical client trajectory
                </div>
                {timelinePoints.map((point, i) => <div key={point.phase} data-index={i} className="flex items-start gap-4">
                    <div className="flex flex-col items-center">
                      <span className="flex size-7 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">
                        {i + 1}
                      </span>
                      {i !== timelinePoints.length - 1 && <span className="w-0.5 h-12 bg-border my-1" />}
                    </div>
                    <div className="space-y-1 pb-1">
                      <div className="flex items-center gap-2">
                        <span className="font-serif font-bold text-sm text-foreground">{point.phase}:</span>
                        <span className="font-sans font-medium text-sm text-primary">{point.title}</span>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {point.desc}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>;
}